/**
 * Zod schemas + inferred types for every backend response the portal consumes
 * (spec §10). Every HTTP body is parsed through these before use (spec §8.4).
 *
 * Success bodies use the common envelope `{ data, requestId }` (spec §10.1);
 * `httpClient` unwraps `data`. The schemas below describe the inner `data`.
 */
import { z } from 'zod';

export const PROVIDERS = ['google', 'linkedin'] as const;
export const providerIdSchema = z.enum(PROVIDERS);
export type ProviderId = z.infer<typeof providerIdSchema>;

export const accountRoleSchema = z.enum(['student', 'teacher', 'parent', 'other']);
export type AccountRole = z.infer<typeof accountRoleSchema>;

export const userAccountStatusSchema = z.enum([
  'ACTIVE',
  'PENDING_ONBOARDING',
  'SUSPENDED',
  'PENDING_REVIEW',
]);
export type UserAccountStatus = z.infer<typeof userAccountStatusSchema>;

// -- Error envelope (spec §10.1) --------------------------------------------
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
  }),
  requestId: z.string(),
});
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

/** Builds an `ApiSuccess<T>` envelope schema around an inner data schema. */
export function envelope<T extends z.ZodTypeAny>(data: T) {
  return z.object({ data, requestId: z.string() });
}

/** Validates the success envelope shape without constraining the inner payload. */
export const envelopeShapeSchema = z.object({
  data: z.unknown(),
  requestId: z.string(),
});

// -- Providers (spec §10.2) -------------------------------------------------
export const providerViewSchema = z.object({
  id: providerIdSchema,
  label: z.string(),
  enabled: z.boolean(),
  priority: z.number(),
  supportsLogin: z.boolean(),
  supportsLink: z.boolean(),
});
export type ProviderView = z.infer<typeof providerViewSchema>;

export const providersResponseSchema = z.object({
  providers: z.array(providerViewSchema),
});
export type ProvidersResponse = z.infer<typeof providersResponseSchema>;

// -- Session (spec §10.3) ---------------------------------------------------
export const sessionUserSchema = z.object({
  id: z.string(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  pictureUrl: z.string().nullable(),
  accountRole: accountRoleSchema.nullable(),
  status: userAccountStatusSchema,
  onboardingCompleted: z.boolean(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const anonymousSessionSchema = z.object({ authenticated: z.literal(false) });

export const authenticatedSessionSchema = z.object({
  authenticated: z.literal(true),
  csrfToken: z.string(),
  user: sessionUserSchema,
  nextUrl: z.string().nullable(),
});

export const sessionResponseSchema = z.discriminatedUnion('authenticated', [
  anonymousSessionSchema,
  authenticatedSessionSchema,
]);
export type SessionResponse = z.infer<typeof sessionResponseSchema>;
export type AuthenticatedSession = z.infer<typeof authenticatedSessionSchema>;

// -- User profile (spec §10.4) ----------------------------------------------
export const userProfileSchema = z.object({
  id: z.string(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  pictureUrl: z.string().nullable(),
  accountRole: accountRoleSchema.nullable(),
  status: userAccountStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  legal: z.object({
    termsAcceptedVersion: z.string().nullable(),
    privacyAcceptedAt: z.string().nullable(),
  }),
});
export type UserProfile = z.infer<typeof userProfileSchema>;

// -- Onboarding (spec §10.5) ------------------------------------------------
export const completeOnboardingResponseSchema = z.object({
  user: userProfileSchema,
  nextUrl: z.string().nullable(),
});
export type CompleteOnboardingResponse = z.infer<typeof completeOnboardingResponseSchema>;

// -- Identities (spec §10.7) ------------------------------------------------
export const connectedIdentitySchema = z.object({
  provider: providerIdSchema,
  linkedAt: z.string(),
  lastAuthenticatedAt: z.string().nullable(),
  emailMasked: z.string().nullable(),
  canUnlink: z.boolean(),
  unlinkDisabledReason: z.enum(['LAST_AUTH_METHOD', 'POLICY']).nullable(),
});
export type ConnectedIdentity = z.infer<typeof connectedIdentitySchema>;

export const identitiesResponseSchema = z.object({
  identities: z.array(connectedIdentitySchema),
  availableToLink: z.array(providerViewSchema),
});
export type IdentitiesResponse = z.infer<typeof identitiesResponseSchema>;

// -- Link start (spec §10.9, Option B) --------------------------------------
export const linkStartResponseSchema = z.object({
  redirectUrl: z.string(),
});
export type LinkStartResponse = z.infer<typeof linkStartResponseSchema>;

// -- Logout all (spec §10.12) -----------------------------------------------
export const logoutAllResponseSchema = z.object({
  revokedSessionCount: z.number(),
});
export type LogoutAllResponse = z.infer<typeof logoutAllResponseSchema>;

// -- Request bodies ---------------------------------------------------------
export type CompleteOnboardingRequest = {
  displayName: string;
  accountRole: AccountRole;
  termsVersion: string;
  privacyAccepted: true;
};

export type UpdateProfileRequest = {
  displayName: string;
  accountRole?: AccountRole;
};

export type LinkStartRequest = {
  client?: string;
  returnRoute?: '/account/connections';
};
